import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, Sparkles, ShieldCheck, Zap, ArrowRight, Briefcase, FileText, UserPlus, LogIn } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CategoryScroller from "@/components/CategoryScroller";
import JobCard from "@/components/JobCard";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { CITIES } from "@/data/categories";
import { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const QUICK_TAGS = ["Uzaktan", "Yazılım", "Satış", "Müşteri Temsilcisi", "Muhasebe"];

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/jobs?limit=6").then((r) => setJobs(r.data.jobs)).catch(() => {});
    api.get("/companies?verified_only=true").then((r) => setCompanies(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const doSearch = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (loc) params.set("location", loc);
    navigate(`/ilanlar?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="isbul.org - Türkiye'nin Yapay Zeka Destekli İş Bulma Platformu"
        description="Binlerce güncel iş ilanı arasından yapay zeka ile size en uygun işi bulun. İş arayanlar ve işverenler için modern, hızlı ve ücretsiz kariyer platformu."
        keywords="iş ilanları, iş bulma, iş ara, eleman, kariyer, işveren, yapay zeka iş eşleştirme"
        canonical="/"
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/60 to-white">
        <div className="max-w-5xl mx-auto px-4 py-16 lg:py-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-blue-100 rounded-full px-4 py-1.5 mb-6 shadow-sm">
            <Sparkles size={15} className="text-blue-600" />
            <span className="text-sm font-medium text-[#1f222d]">Yapay zeka destekli iş eşleştirme</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1f222d] leading-[1.1]">
            Hayalindeki işi <span className="text-blue-600">isbul.org</span> ile bul
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Binlerce ilan arasından sana en uygun pozisyonları yapay zeka seçsin. İşverenler için hızlı, iş arayanlar için akıllı.
          </p>

          <div className="mt-9 bg-white rounded-2xl shadow-[0_12px_40px_rgb(0,0,0,0.08)] border border-slate-100 p-3 flex flex-col sm:flex-row gap-2 max-w-3xl mx-auto">
            <div className="flex items-center gap-2 flex-1 px-3">
              <Search size={20} className="text-slate-400 shrink-0" />
              <input
                data-testid="hero-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doSearch()}
                placeholder="Pozisyon, şirket veya anahtar kelime"
                className="w-full py-3 outline-none text-[#1f222d] placeholder:text-slate-400 bg-transparent"
              />
            </div>
            <div className="flex items-center gap-2 sm:border-l border-slate-100 px-3">
              <MapPin size={20} className="text-slate-400 shrink-0" />
              <select
                data-testid="hero-location-select"
                value={loc}
                onChange={(e) => setLoc(e.target.value)}
                className="w-full sm:w-36 py-3 outline-none text-[#1f222d] bg-transparent"
              >
                <option value="">Tüm Şehirler</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Button data-testid="hero-search-btn" onClick={doSearch} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-6 text-base font-semibold">
              İş Ara
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            <span className="text-sm text-slate-400">Popüler:</span>
            {QUICK_TAGS.map((t) => (
              <button key={t} data-testid={`quick-tag-${t}`} onClick={() => navigate(`/ilanlar?q=${encodeURIComponent(t)}`)} className="text-sm px-3 py-1 rounded-full bg-white border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-600 transition-colors">
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pt-10">
        <div className="grid sm:grid-cols-2 gap-4">
          <button data-testid="home-search-jobs" onClick={() => navigate("/ilanlar")} className="group text-left bg-blue-600 hover:bg-blue-700 rounded-2xl p-6 text-white transition-colors flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2"><Search size={22} /><h3 className="text-xl font-bold">İş Ara</h3></div>
              <p className="text-blue-100 text-sm mt-1">Binlerce ilan arasında sana uygun olanı bul</p>
            </div>
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            data-testid="home-applications"
            onClick={() => navigate(user ? (user.role === "employer" ? "/isveren?tab=applications" : "/panel?tab=applications") : "/giris")}
            className="group text-left bg-white border-2 border-[#1f222d] hover:bg-[#1f222d] hover:text-white rounded-2xl p-6 text-[#1f222d] transition-colors flex items-center justify-between"
          >
            <div>
              <div className="flex items-center gap-2"><FileText size={22} /><h3 className="text-xl font-bold">Başvurularım</h3></div>
              <p className="text-sm mt-1 opacity-70">Başvurularını ve durumlarını takip et</p>
            </div>
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <CategoryScroller categories={cats} />
      </section>

      {/* AI Feature */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        <div className="rounded-3xl bg-[#1f222d] text-white p-8 lg:p-14 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 mb-5">
                <Sparkles size={15} className="text-blue-300" />
                <span className="text-sm font-medium">Yapay Zeka Eşleştirme</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold leading-tight">CV'ni yükle, AI senin için en uygun işleri bulsun</h2>
              <p className="mt-4 text-slate-300 leading-relaxed">
                Profilini doldur veya CV'ni yükle; yapay zeka işverenlerin kriterleriyle karşılaştırıp uyum skorunu hesaplar ve sana bildirim gönderir.
              </p>
              <Button data-testid="hero-ai-btn" onClick={() => navigate("/ai-eslestirme")} className="mt-7 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-7 py-6 text-base font-semibold">
                AI ile İş Bul <ArrowRight size={18} className="ml-1" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Zap, t: "Anında Eşleşme", d: "Saniyeler içinde uyum skorun hesaplanır" },
                { icon: Sparkles, t: "Akıllı Öneri", d: "Kriterlere göre kişiselleştirilmiş ilanlar" },
                { icon: ShieldCheck, t: "Onaylı Markalar", d: "Mavi tikli güvenilir işverenler" },
                { icon: Search, t: "Gelişmiş Filtre", d: "Şehir, kategori ve çalışma türüne göre" },
              ].map((f, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <f.icon size={22} className="text-blue-400 mb-3" />
                  <h4 className="font-semibold text-sm">{f.t}</h4>
                  <p className="text-xs text-slate-400 mt-1">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest jobs */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1f222d]">Güncel İş İlanları</h2>
          <button data-testid="view-all-jobs" onClick={() => navigate("/ilanlar")} className="text-blue-600 font-semibold text-sm hover:underline">Tümünü Gör →</button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.map((j) => <JobCard key={j.id} job={j} />)}
        </div>
      </section>

      {/* Verified companies */}
      {companies.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 py-6 pb-16">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1f222d] mb-6">Onaylanmış Markalar</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {companies.map((c) => (
              <button key={c.id} data-testid={`home-company-${c.id}`} onClick={() => navigate(`/sirket/${c.id}`)} className="bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-xl bg-slate-50 flex items-center justify-center overflow-hidden mb-3">
                  {c.logo_file_id ? <img src={fileUrl(c.logo_file_id)} alt={c.company_name} className="w-full h-full object-contain" /> : <span className="text-xl font-bold text-blue-600">{c.company_name[0]}</span>}
                </div>
                <div className="flex items-center gap-1 font-semibold text-[#1f222d] text-sm">
                  <span className="truncate">{c.company_name}</span>
                  <VerifiedBadge size={15} />
                </div>
                <span className="text-xs text-slate-400 mt-1">{c.job_count} ilan</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Bottom auth / profile CTA */}
      {!user ? (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-blue-50 to-white p-8 lg:p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1f222d]">Hemen üye ol, kariyerine başla</h2>
            <p className="text-slate-600 mt-2 max-w-xl mx-auto">İş arayan ya da işveren olarak ücretsiz kaydol; ilanlara başvur, AI eşleştirmeyi kullan.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-7">
              <Button data-testid="home-register-btn" onClick={() => navigate("/kayit")} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-base font-semibold">
                <UserPlus size={18} className="mr-2" /> Kaydol
              </Button>
              <Button data-testid="home-login-btn" onClick={() => navigate("/giris")} variant="outline" className="rounded-full px-8 py-6 text-base font-semibold border-2 border-[#1f222d] text-[#1f222d]">
                <LogIn size={18} className="mr-2" /> Giriş Yap
              </Button>
            </div>
          </div>
        </section>
      ) : user.role === "jobseeker" ? (
        <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-16">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 lg:p-8 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <h2 className="text-xl font-bold text-[#1f222d]">Hoş geldin, {user.name} 👋</h2>
              <p className="text-slate-500 mt-1">Profilini güncelle, CV yükle ve AI ile sana en uygun işleri bul.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/panel"><Button data-testid="home-profile-btn" variant="outline" className="rounded-full border-2 border-[#1f222d] text-[#1f222d] px-6"><FileText size={16} className="mr-2" /> Profilim</Button></Link>
              <Link to="/ai-eslestirme"><Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6"><Sparkles size={16} className="mr-2" /> AI ile İş Bul</Button></Link>
            </div>
          </div>
        </section>
      ) : null}

      <Footer />
    </div>
  );
}
